import React, { useState } from 'react';
import Papa from 'papaparse';
import { Upload, FileText } from 'lucide-react';
import { UnimedAlert, UnimedAlertDescription } from './ui/UnimedComponents';

const FileUpload = ({ onDataLoad, loading }) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);

  const handleFiles = (files) => {
    if (files && files.length > 0) {
      const file = files[0];
      
      if (!file.name.endsWith('.csv')) {
        setError('Por favor, selecione um arquivo CSV.');
        return;
      }

      setError(null);
      
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            setError('Erro ao processar arquivo CSV: ' + results.errors[0].message);
            return;
          }

          // Validate required columns
          const requiredColumns = [
            'ATENDIMENTO',
            'DATA/HORA DO ATENDIMENTO', 
            'DATA/HORA DA SAPS3',
            'SAPS3 SCORE',
            '% SAPS3',
            'DATA/HORA DO ÓBITO'
          ];

          const headers = Object.keys(results.data[0] || {});
          const missingColumns = requiredColumns.filter(col => !headers.includes(col));
          
          if (missingColumns.length > 0) {
            setError(`Colunas obrigatórias não encontradas: ${missingColumns.join(', ')}`);
            return;
          }

          onDataLoad(results.data);
        },
        error: (error) => {
          setError('Erro ao ler arquivo: ' + error.message);
        }
      });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto p-6">
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 transition-all duration-300 ${
          dragActive 
            ? 'border-unimed-green-400 bg-unimed-green-50 shadow-lg scale-105' 
            : 'border-unimed-beige-300 hover:border-unimed-green-300 hover:bg-unimed-beige-50'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept=".csv"
          onChange={handleChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={loading}
        />
        
        <div className="text-center">
          <div className={`mx-auto w-16 h-16 rounded-xl flex items-center justify-center mb-4 ${
            dragActive ? 'bg-unimed-green-500' : 'bg-gradient-to-br from-unimed-green-500 to-unimed-green-600'
          } shadow-lg`}>
            <Upload className="h-8 w-8 text-white" />
          </div>
          <p className="unimed-titulo-secundario text-lg text-unimed-darkgreen-800 mb-2">
            {loading ? (
              <span className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-unimed-green-500 mr-2"></div>
                PROCESSANDO ARQUIVO...
              </span>
            ) : (
              <>
                <span className="unimed-titulo-principal">CLIQUE PARA SELECIONAR</span><br/>
                <span className="unimed-texto-corrido text-sm text-unimed-darkgreen-600">ou arraste seu arquivo CSV aqui</span>
              </>
            )}
          </p>
          <p className="unimed-texto-corrido text-xs text-unimed-darkgreen-500 mt-2 leading-relaxed">
            Arquivo deve conter as colunas obrigatórias:<br/>
            <span className="font-medium">ATENDIMENTO, DATA/HORA DO ATENDIMENTO, DATA/HORA DA SAPS3, SAPS3 SCORE, % SAPS3, DATA/HORA DO ÓBITO</span>
          </p>
        </div>
      </div>

      {error && (
        <div className="mt-6">
          <UnimedAlert variant="error">
            <UnimedAlertDescription>
              <div className="unimed-titulo-secundario font-medium mb-1">ERRO NO PROCESSAMENTO</div>
              <p className="unimed-texto-corrido text-sm">{error}</p>
            </UnimedAlertDescription>
          </UnimedAlert>
        </div>
      )}

      <div className="mt-8">
        <div className="bg-unimed-beige-50 rounded-xl p-6 border border-unimed-beige-200">
          <div className="flex items-center mb-4">
            <div className="bg-unimed-lightblue-500 p-2 rounded-lg mr-3">
              <FileText className="h-4 w-4 text-white" />
            </div>
            <span className="unimed-titulo-secundario font-semibold text-unimed-darkgreen-800">FORMATO ESPERADO DO ARQUIVO</span>
          </div>
          <div className="bg-white p-4 rounded-lg border border-unimed-beige-300 font-mono text-xs text-unimed-darkgreen-700 overflow-x-auto">
            <div className="unimed-chapeu text-unimed-green-700 mb-2">CABEÇALHO (PRIMEIRA LINHA):</div>
            ATENDIMENTO,DATA/HORA DO ATENDIMENTO,DATA/HORA DA SAPS3,SAPS3 SCORE,% SAPS3,DATA/HORA DO ÓBITO<br/><br/>
            <div className="unimed-chapeu text-unimed-orange-700 mb-2">EXEMPLOS DE DADOS:</div>
            12345,01/01/2024 10:30,01/01/2024 12:00,45,15.2,<br/>
            12346,01/01/2024 14:15,01/01/2024 16:00,62,28.7,02/01/2024 08:30<br/>
            <span className="text-unimed-darkgreen-500">...</span>
          </div>
          <p className="unimed-texto-corrido text-xs text-unimed-darkgreen-600 mt-3 leading-relaxed">
            <strong>Dica:</strong> Deixe a coluna "DATA/HORA DO ÓBITO" vazia para pacientes que sobreviveram.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
